import { supabase } from '../lib/supabase'
import { treatmentOf, gstComponent, GST_TREATMENT_LABEL } from '../lib/gstTreatment'
import { Deal } from '../types'
import { SIGNED_STATUSES, ON_HAND_STATUSES, SOLD_STATUSES } from '../lib/dealLifecycle'

/**
 * Phase 5 — Reporting service.
 * Contracts + Month-End reports, wired to real staging data.
 * Everything is derived from existing tables (deals, invoices,
 * bill_of_sale_documents, deal_status_log) plus the Phase-5
 * capitalised-cost columns on deals.
 */

// ---------- shared helpers ----------

const dYear = (d: Deal) => d.autograb_year ?? d.vehicle_year
const dMake = (d: Deal) => d.autograb_make || d.vehicle_make || ''
const dModel = (d: Deal) => d.autograb_model || d.vehicle_model || ''
const dVin = (d: Deal) => d.autograb_vin || d.vehicle_vin || ''
/*
 * The stock number where the deal has one, otherwise the first six characters of the
 * id. Same rule as the GST register, so one car reads the same on both screens.
 */
const stockNumberOf = (d: Deal) => (d.stock_number != null ? String(d.stock_number) : d.id.slice(0, 6).toUpperCase())
const vehicleLabel = (d: Deal) => `${dYear(d) ?? ''} ${dMake(d)} ${dModel(d)}`.trim()

/**
 * Election path.
 *
 * The Framework used to describe three paths built on two elections, deposit
 * and lodgement. v2.0-NL.2.0 removed the deposit, so there are two: lodgement
 * elected, or not. The `deposit` parameter is kept so historical rows that
 * still carry a deposit read the way they were signed.
 */
export function electionPath(deposit?: boolean, lodgement?: boolean): { code: string; label: string } {
  if (lodgement) return { code: 'path1', label: 'Path 1 (Lodgement)' }
  if (deposit) return { code: 'path2', label: 'Path 2 (legacy deposit)' }
  return { code: 'path3', label: 'Path 3 (no lodgement)' }
}

const daysBetween = (a: string, b: string) =>
  Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86_400_000)

const daysSince = (a: string) => Math.floor((Date.now() - new Date(a).getTime()) / 86_400_000)

/* All three lists now come from ../lib/dealLifecycle. Held locally, they had already
   drifted from the pipeline: sold_in_transit and sold_delivered were in neither the
   signed nor the sold list, and deal_pending was not counted as on-hand stock, so a
   car in any of those three stages fell out of the P&L and the month-end entirely. */

// ==========================================================================
// CONTRACTS REPORT
// ==========================================================================

export interface BosRegisterRow {
  dealId: string
  vehicle: string
  vin: string
  buyer: string
  electionCode: string
  electionLabel: string
  signedAt: string | null
  ppsrRef: string | null
  complete: boolean
}

export interface ArRegisterRow {
  dealId: string
  vehicle: string
  buyer: string
  contractDate: string | null
  termsDays: number | null
  invoiceIssued: string | null
  dueDate: string | null
  status: 'paid' | 'in_progress' | 'overdue' | 'draft'
}

export interface PendingSignatureRow {
  dealId: string
  vehicle: string
  buyer: string
  createdAt: string
  daysPending: number
}

export interface ContractsReport {
  stats: { total: number; signed: number; ppsrLodged: number; pending: number }
  bosRegister: BosRegisterRow[]
  arRegister: ArRegisterRow[]
  pending: PendingSignatureRow[]
}

export async function loadContractsReport(): Promise<ContractsReport> {
  const [{ data: deals, error: dErr }, { data: bos, error: bErr }, { data: invoices, error: iErr }] =
    await Promise.all([
      supabase.from('deals').select('*').is('deleted_at', null),
      supabase.from('bill_of_sale_documents').select('*').is('deleted_at', null),
      supabase.from('invoices').select('*'),
    ])
  if (dErr) throw dErr
  if (bErr) throw bErr
  if (iErr) throw iErr

  const dealList = (deals || []) as Deal[]
  const byId = new Map(dealList.map((d) => [d.id, d]))
  const bosByDeal = new Map<string, any>()
  ;(bos || []).forEach((b: any) => bosByDeal.set(b.deal_id, b))

  // Vehicle Purchase Agreement Register — deals with a signed BoS (binding contract).
  const bosRegister: BosRegisterRow[] = dealList
    .filter((d) => {
      const b = bosByDeal.get(d.id)
      return SIGNED_STATUSES.includes(d.deal_status) || b?.seller_signed_at || b?.both_signed
    })
    .map((d) => {
      const b = bosByDeal.get(d.id)
      const deposit = b?.deposit_elected ?? d.deposit_elected
      const lodgement = b?.lodgement_elected ?? d.lodgement_elected
      const ep = electionPath(deposit, lodgement)
      return {
        dealId: d.id,
        vehicle: vehicleLabel(d),
        vin: dVin(d),
        buyer: d.buyer_name || d.buyer_company || '—',
        electionCode: ep.code,
        electionLabel: ep.label,
        signedAt: b?.both_signed_at || b?.seller_signed_at || null,
        ppsrRef: d.ppsr_reference_number || null,
        complete: !!(d.ppsr_reference_number && (b?.both_signed || b?.seller_signed_at)),
      }
    })
    .sort((a, b) => (b.signedAt || '').localeCompare(a.signedAt || ''))

  // AR Contract Register — invoiced deals with payment terms.
  const arRegister: ArRegisterRow[] = (invoices || [])
    .map((inv: any): ArRegisterRow => {
      const d = byId.get(inv.deal_id)
      const terms = inv.invoice_date && inv.due_date ? daysBetween(inv.invoice_date, inv.due_date) : null
      let status: ArRegisterRow['status'] = 'in_progress'
      if (inv.payment_received_at || inv.status === 'paid') status = 'paid'
      else if (inv.status === 'draft') status = 'draft'
      else if (inv.due_date && new Date(inv.due_date).getTime() < Date.now()) status = 'overdue'
      return {
        dealId: inv.deal_id,
        vehicle: d ? vehicleLabel(d) : inv.invoice_number,
        buyer: inv.buyer_name || inv.buyer_company || d?.buyer_name || '—',
        contractDate: d?.elections_locked_at || d?.created_at || null,
        termsDays: terms,
        invoiceIssued: inv.invoice_date || null,
        dueDate: inv.due_date || null,
        status,
      }
    })
    .sort((a, b) => (b.invoiceIssued || '').localeCompare(a.invoiceIssued || ''))

  // Pending signatures — BoS sent but not yet signed.
  const pending: PendingSignatureRow[] = dealList
    .filter((d) => {
      const b = bosByDeal.get(d.id)
      const sent = d.bill_of_sale_sent_at
      const signed = b?.seller_signed_at || b?.both_signed
      return sent && !signed && !['cancelled', 'closed', 'sold'].includes(d.deal_status)
    })
    .map((d) => ({
      dealId: d.id,
      vehicle: vehicleLabel(d),
      buyer: d.buyer_name || d.buyer_company || '—',
      createdAt: d.bill_of_sale_sent_at || d.created_at,
      daysPending: daysSince(d.bill_of_sale_sent_at || d.created_at),
    }))
    .sort((a, b) => b.daysPending - a.daysPending)

  return {
    stats: {
      total: dealList.filter((d) => !['sourced', 'cancelled'].includes(d.deal_status)).length,
      signed: bosRegister.length,
      ppsrLodged: bosRegister.filter((r) => r.ppsrRef).length,
      pending: pending.length,
    },
    bosRegister,
    arRegister,
    pending,
  }
}

// ==========================================================================
// MONTH-END REPORT
// ==========================================================================

/** §4.4/§6.6 now decides the credit; see lib/gstTreatment. */
const ACQUIRED_METHOD_LABEL: Record<string, string> = {
  purchase: 'Purchase',
  auction: 'Auction',
  trade_in: 'Trade-in',
  consignment: 'Consignment',
}

/* One line of the month-end totals, for one GST treatment. */
export interface TaxTypeTotal {
  treatment: string
  vehicles: number
  buyIn: number
  capitalised: number
  gstClaimable: number
  landedCost: number
}

export interface StocktakeRow {
  dealId: string
  vehicle: string
  vin: string
  stockNumber: string
  acquiredAt: string | null
  /** Days on hand as at the end of the period, not as at today. */
  daysOnHand: number
  ageBucket: AgeBucket
  buyIn: number
  capitalised: number
  gstClaimable: number
  landedCost: number
  hasTaxInvoice: boolean
  gstTreatment: string
  salespersonId: string | null
  salesperson: string
}

/** §6.2: vehicles on hand counted by age interval. */
export type AgeBucket = '0_7' | '8_14' | '15_30' | '30_plus'

export const AGE_BUCKET_LABEL: Record<AgeBucket, string> = {
  '0_7': '0–7 days',
  '8_14': '8–14 days',
  '15_30': '15–30 days',
  '30_plus': '30+ days',
}

export function ageBucketOf(days: number): AgeBucket {
  if (days <= 7) return '0_7'
  if (days <= 14) return '8_14'
  if (days <= 30) return '15_30'
  return '30_plus'
}

export interface AcquisitionRow {
  dealId: string
  vehicle: string
  seller: string
  /** §6.3: how the vehicle was acquired — purchase, auction, trade-in… */
  method: string
  /** §6.3: which GST treatment the spend sits under. */
  taxType: string
  purchasePrice: number
  capitalisedTransport: number
  capitalisedRepairs: number
  totalCost: number
  status: string
}

/** §6.3: spend grouped by who it went to. */
export interface SupplierSpendRow {
  supplier: string
  vehicles: number
  spend: number
}

export interface MonthEndReport {
  periodLabel: string
  periodMonth: string
  stats: { onHand: number; landedCost: number; notionalGst: number }
  stocktake: StocktakeRow[]
  ageing: { bucket: AgeBucket; label: string; vehicles: number; landedCost: number }[]
  totals: { buyIn: number; capitalised: number; landedCost: number; byTaxType: TaxTypeTotal[] }
    /* Winnie 1 Sep: the working behind the closing stock figure. */
    rollForward: {
      openingStock: number
      openingVehicles: number
      netPurchases: number
      purchasedVehicles: number
      reconditioning: number
      costOfGoodsSold: number
      soldVehicles: number
      closingStock: number
      closingVehicles: number
      variance: number
    }
  journal: {
    debitAccount: string
    creditAccount: string
    amount: number
    reverseDate: string
    reverseOn: string
    basExcluded: boolean
    vehicleCount: number
  }
  acquisition: AcquisitionRow[]
  supplierSpend: SupplierSpendRow[]
  acquisitionTotals: { spend: number; purchaseOnly: number; count: number }
  salespeople: { id: string; name: string }[]
}

/**
 * monthKey format: 'YYYY-MM' (defaults to current month).
 *
 * `salespersonId` filters the stocktake to one person's vehicles (§6.2). The
 * stocktake also used to ignore the chosen month entirely — it filtered on the
 * deal's *current* status with no date bound, so picking June still returned
 * today's stock. It now asks what was on hand at the end of the period.
 */
/**
 * Winnie, v4 of the change register: "Custom period filters — choose specific
 * date, not for full month."
 *
 * A month-end close is not always a calendar month. A stocktake taken to the
 * 15th, or a period that runs across a month boundary, could not be asked for at
 * all: the only lever was a dropdown of whole months. `range` overrides the
 * month when it is given, and `to` is INCLUSIVE — a person asking for the 30th
 * means the 30th, not up to the morning of it.
 */
export interface ReportRange {
  from?: string | null
  to?: string | null
}

export async function loadMonthEndReport(
  monthKey?: string,
  salespersonId?: string | null,
  range?: ReportRange | null
): Promise<MonthEndReport> {
  const now = new Date()
  const [yy, mm] = monthKey && monthKey !== 'custom'
    ? monthKey.split('-').map(Number)
    : [now.getFullYear(), now.getMonth() + 1]
  let start = new Date(Date.UTC(yy, mm - 1, 1))
  let end = new Date(Date.UTC(yy, mm, 1)) // exclusive
  let periodLabel = start.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' })

  if (range && (range.from || range.to)) {
    const atUtc = (s: string) => new Date(s + 'T00:00:00Z')
    if (range.from) start = atUtc(range.from)
    if (range.to) {
      end = atUtc(range.to)
      end.setUTCDate(end.getUTCDate() + 1) // make `to` inclusive
    }
    const day = (d: Date) =>
      d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    const lastDay = new Date(end)
    lastDay.setUTCDate(lastDay.getUTCDate() - 1)
    periodLabel = `${day(start)} – ${day(lastDay)}`
  }

  const { data, error } = await supabase.from('deals').select('*').is('deleted_at', null)
  if (error) throw error
  const deals = (data || []) as Deal[]

  // Attributions, so the stocktake can be filtered by salesperson (§6.2).
  const [{ data: attrs }, { data: mems }] = await Promise.all([
    supabase.from('deal_attributions').select('deal_id, team_member_id, role'),
    supabase.from('team_members').select('id, name').order('name'),
  ])
  const memberName = new Map<string, string>((mems || []).map((m: any) => [m.id, m.name]))
  // A deal can be credited to more than one person; the sales attribution is
  // the one that owns the stock for reporting purposes, with any attribution as
  // a fallback so nothing disappears from a filtered view.
  const ownerOf = new Map<string, string>()
  for (const a of (attrs || []) as any[]) {
    if (!ownerOf.has(a.deal_id) || a.role === 'sales') ownerOf.set(a.deal_id, a.team_member_id)
  }

  const capOf = (d: Deal) => (d.capitalised_transport || 0) + (d.capitalised_repairs || 0)
  const buyInOf = (d: Deal) => d.valuation_amount || 0
  const acquiredAtOf = (d: Deal) => d.acquired_date || d.paid_at || d.created_at || null

  // GST claimable on the acquisition now follows §4.4 rather than dividing
  // everything by eleven — a vehicle bought on a tax invoice and one bought
  // privately have different landed costs.
  const gstOf = (d: Deal) => {
    const t = treatmentOf(d)
    return t === 'no_gst' ? 0 : gstComponent(buyInOf(d))
  }
  const landedOf = (d: Deal) => buyInOf(d) + capOf(d) - gstOf(d)

  const periodEnd = new Date(end.getTime() - 1)

  /*
     On hand as at a moment in time: acquired on or before it, and either still
     in stock or sold after it.

     Written as a function of the cut-off rather than inline, because the closing
     balance, the opening balance and the roll-forward below all need exactly this
     rule. Three hand-written copies of it is how a stock ledger stops balancing.

     A deal whose status says sold but which carries NO sale date is still stock.
     The status disqualifies it and there is no date to prove it left, so without
     this it would fall out of closing stock AND out of every month's cost of goods
     sold, which keys off the same missing date. A car has not left stock until
     something records that it left. The missing date is a data defect and is worth
     reporting on its own rather than being silently absorbed into a number
     somebody lodges.
  */
  const soldAt = (d: Deal) => d.sold_at || d.closed_at || null

  const onHandAsAt = (cutoffMs: number) =>
    deals.filter((d) => {
      const acquired = acquiredAtOf(d)
      if (!acquired || new Date(acquired).getTime() > cutoffMs) return false
      if (d.deal_status === 'cancelled') return false
      const gone = soldAt(d)
      if (gone && new Date(gone).getTime() <= cutoffMs) return false
      if (!gone && SOLD_STATUSES.includes(d.deal_status)) return true
      return ON_HAND_STATUSES.includes(d.deal_status) || !!gone
    })

  const onHand = onHandAsAt(periodEnd.getTime())

  /* One salesperson filter, applied the same way everywhere it is needed. */
  const mine = (list: Deal[]) =>
    salespersonId ? list.filter((d) => ownerOf.get(d.id) === salespersonId) : list

  const filteredOnHand = mine(onHand)

  const daysOnHandOf = (d: Deal) => {
    const acquired = acquiredAtOf(d)
    if (!acquired) return 0
    return Math.max(
      0,
      Math.round((periodEnd.getTime() - new Date(acquired).getTime()) / 86_400_000)
    )
  }

  const stocktake: StocktakeRow[] = filteredOnHand
    .map((d) => {
      const days = daysOnHandOf(d)
      const owner = ownerOf.get(d.id) || null
      return {
        dealId: d.id,
        vehicle: vehicleLabel(d),
        vin: dVin(d),
        stockNumber: stockNumberOf(d),
        acquiredAt: acquiredAtOf(d),
        daysOnHand: days,
        ageBucket: ageBucketOf(days),
        buyIn: buyInOf(d),
        capitalised: capOf(d),
        gstClaimable: gstOf(d),
        landedCost: landedOf(d),
        hasTaxInvoice: treatmentOf(d) === 'tax_invoice',
        gstTreatment: GST_TREATMENT_LABEL[treatmentOf(d)],
        salespersonId: owner,
        salesperson: owner ? memberName.get(owner) || '—' : 'Unattributed',
      }
    })
    .sort((a, b) => b.daysOnHand - a.daysOnHand)

  const ageing = (['0_7', '8_14', '15_30', '30_plus'] as AgeBucket[]).map((bucket) => {
    const rows = stocktake.filter((r) => r.ageBucket === bucket)
    return {
      bucket,
      label: AGE_BUCKET_LABEL[bucket],
      vehicles: rows.length,
      landedCost: rows.reduce((s, r) => s + r.landedCost, 0),
    }
  })

  const totalLanded = stocktake.reduce((s, r) => s + r.landedCost, 0)
  const totalBuyIn = stocktake.reduce((s, r) => s + r.buyIn, 0)
  const totalCap = stocktake.reduce((s, r) => s + r.capitalised, 0)
  const notionalGst = stocktake.reduce((s, r) => s + r.gstClaimable, 0)

  const reverse = new Date(Date.UTC(yy, mm, 1))
  const reverseDate = reverse.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })

  // Acquisition report — vehicles acquired within the period.
  const inPeriod = (iso?: string | null) => {
    if (!iso) return false
    const t = new Date(iso).getTime()
    return t >= start.getTime() && t < end.getTime()
  }
  const acquired = deals.filter((d) => inPeriod(acquiredAtOf(d)))
  const acquisition: AcquisitionRow[] = acquired
    .map((d) => ({
      dealId: d.id,
      vehicle: vehicleLabel(d),
      seller: d.seller_name,
      method: ACQUIRED_METHOD_LABEL[d.acquired_method || ''] || 'Purchase',
      taxType: GST_TREATMENT_LABEL[treatmentOf(d)],
      purchasePrice: buyInOf(d),
      capitalisedTransport: d.capitalised_transport || 0,
      capitalisedRepairs: d.capitalised_repairs || 0,
      totalCost: buyInOf(d) + capOf(d),
      status: d.deal_status,
    }))
    .sort((a, b) => a.vehicle.localeCompare(b.vehicle))

  // §6.3: spend per supplier. Grouped on the seller name, which is the only
  // identity a seller has until there is a supplier record.
  const bySupplier = new Map<string, SupplierSpendRow>()
  for (const row of acquisition) {
    const key = row.seller || '—'
    const cur = bySupplier.get(key) || { supplier: key, vehicles: 0, spend: 0 }
    cur.vehicles += 1
    cur.spend += row.purchasePrice
    bySupplier.set(key, cur)
  }
  const supplierSpend = [...bySupplier.values()].sort((a, b) => b.spend - a.spend)

  /*
   * Stock on hand split by GST treatment. Winnie's document asks for the month's
   * totals broken down by tax type. The split is taken off the same rows the table
   * renders, so the two cannot disagree the moment a treatment is corrected.
   */
  const byTreatment = new Map<string, TaxTypeTotal>()
  for (const r of stocktake) {
    const acc = byTreatment.get(r.gstTreatment) || {
      treatment: r.gstTreatment,
      vehicles: 0,
      buyIn: 0,
      capitalised: 0,
      gstClaimable: 0,
      landedCost: 0,
    }
    acc.vehicles += 1
    acc.buyIn += r.buyIn
    acc.capitalised += r.capitalised
    acc.gstClaimable += r.gstClaimable
    acc.landedCost += r.landedCost
    byTreatment.set(r.gstTreatment, acc)
  }
  const taxTypeTotals = [...byTreatment.values()].sort((a, b) => b.landedCost - a.landedCost)
  /*
     Winnie, 1 Sep: "Closing Stock = Opening Stock + Net Purchases (buy-in cost)
     + Direct Reconditioning Costs - Cost of Goods Sold (COGS)".

     The report showed a closing stock figure and never showed the working, so a
     figure that disagreed with EasyCars could not be argued with — only disbelieved.
     This publishes the four movements behind it, on the same rows and the same
     landed-cost basis as the stocktake above, so the number can be reconciled line
     by line instead of taken on trust.

     Net purchases is the buy-in net of the GST claimable on it, and reconditioning
     is the capitalised spend, so the two together are exactly the landed cost that
     enters stock. Splitting them that way is what Winnie asked for and it also
     stops the reconditioning being counted twice.

     Reconditioning carries no date of its own on the deal, so it is attributed to
     the month the vehicle was acquired. That is the only basis available today and
     it is stated on the panel rather than left to be inferred.

     Variance is shown rather than forced to zero. A vehicle that is counted as a
     purchase but is neither in closing stock nor in COGS — one acquired but not yet
     paid for, say — lands here in the open where somebody can see it, instead of
     being quietly netted off.
  */
  const openingRows = mine(onHandAsAt(start.getTime() - 1))
  const acquiredInPeriod = mine(
    deals.filter((d) => inPeriod(acquiredAtOf(d)) && d.deal_status !== 'cancelled'),
  )
  const soldInPeriod = mine(
    deals.filter((d) => inPeriod(soldAt(d)) && d.deal_status !== 'cancelled'),
  )

  const sum = (list: Deal[], f: (d: Deal) => number) => list.reduce((s, d) => s + f(d), 0)

  const openingStock = sum(openingRows, landedOf)
  const netPurchases = sum(acquiredInPeriod, (d) => buyInOf(d) - gstOf(d))
  const reconditioning = sum(acquiredInPeriod, capOf)
  const costOfGoodsSold = sum(soldInPeriod, landedOf)
  const closingStock = totalLanded

  const rollForward = {
    openingStock,
    openingVehicles: openingRows.length,
    netPurchases,
    purchasedVehicles: acquiredInPeriod.length,
    reconditioning,
    costOfGoodsSold,
    soldVehicles: soldInPeriod.length,
    closingStock,
    closingVehicles: filteredOnHand.length,
    variance:
      openingStock + netPurchases + reconditioning - costOfGoodsSold - closingStock,
  }

  return {
    periodLabel,
    periodMonth: `${yy}-${String(mm).padStart(2, '0')}-01`,
    stats: { onHand: stocktake.length, landedCost: totalLanded, notionalGst },
    stocktake,
    ageing,
    totals: { buyIn: totalBuyIn, capitalised: totalCap, landedCost: totalLanded, byTaxType: taxTypeTotals },
    rollForward,
    journal: {
      debitAccount: '631',
      creditAccount: '355',
      /*
         Winnie, 1 Sep: "Xero journal should be Total landed cost + Notional GST".

         Landed cost is carried net of the GST claimable on the acquisition, but under
         Division 66 that credit is not claimable until the vehicle is SOLD. A car still
         on hand at month end is therefore worth what was actually paid for it, credit
         included — so the closing-stock journal adds the notional GST back.
      */
      amount: Math.round((totalLanded + notionalGst) * 100) / 100,
      reverseDate,
      reverseOn: reverse.toISOString().slice(0, 10),
      basExcluded: true,
      vehicleCount: stocktake.length,
    },
    acquisition,
    supplierSpend,
    salespeople: (mems || []).map((m: any) => ({ id: m.id, name: m.name })),
    acquisitionTotals: {
      // §6.3 says purchase amounts only — the old total quietly folded in
      // transport and repairs, which made "raw seller spend" not raw at all.
      spend: acquisition.reduce((s, r) => s + r.purchasePrice, 0),
      purchaseOnly: acquisition.reduce((s, r) => s + r.purchasePrice, 0),
      count: acquisition.length,
    },
  }
}

/** Recent month options for the Month-End picker: [{key,label}] newest first. */
export function recentMonths(count = 6): { key: string; label: string }[] {
  const now = new Date()
  const out: { key: string; label: string }[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getFullYear(), now.getMonth() - i, 1))
    out.push({
      key: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    })
  }
  return out
}

/** CSV helpers for the Export buttons. */
export function toCsv(headers: string[], rows: (string | number | null)[][]): string {
  const esc = (v: string | number | null) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return [headers.join(','), ...rows.map((r) => r.map(esc).join(','))].join('\n')
}

export function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
